// Added by Sejune Oh on 2019-02-15
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { configure } from 'mobx'
// User Stores
import { UserPrefStore } from './user/UserPref'
import { UserAuthStore } from './user/UserAuth'

// Global Stores
import { HistoryStore, ConnectionStore } from './global'
import EventStore from './EventStore'
import CLAppStore from './CLAppStore'
import ProductStore from './ProductStore'

// Registry Stores
import RegistryStore from './registry/RegistryStore'

// Management Stores
import { UserStore } from './mgmt/user'
import { GroupStore } from './mgmt/group'
import { DataStorageStore } from '@/stores/mgmt/dataStorage'
import { NodeStore } from './mgmt/node'
import { DicomScpStore } from './mgmt/dicomScp'
import DicomPeerStore from './mgmt/DicomPeerStore'
import { LicenseStore } from './mgmt/license'
import { APSRuleStore, APSBase } from './mgmt/apsRule'
import { DicomScpRulesetBase, DicomScpRulesetStore } from './mgmt/dicomScpRule'
import { SiteConfigStore, PackageConfigStore, SystemConfigStore } from './mgmt/config'
import { GatewayStore } from './mgmt/gateway'
import { NotificationStore } from './mgmt/notification'
import { HL7Store } from './mgmt/hl7'
import { WPSStore } from './mgmt/wps'
import { PacsQRStore } from './mgmt/pacsqr'

// DataStorage Stores
import DataStorageStudyStore from './dataStorage/dsStudyStore'
import DataStorageSopInstanceStore from './dataStorage/dsSopInstanceStore'
import DataStorageSeriesStore from './dataStorage/dsSeriesStore'
import DataStoragePatientStore from './dataStorage/dsPatientStore'
import DataStorageDataServiceStore from './dataStorage/dsDataServiceStore'
import DataStorageConfigVolumeDiskStore from './dataStorage/config/dsConfigVolumeDiskStore'
import DataStorageConfigIncomingVolumeDiskStore from './dataStorage/config/dsConfigIncomingVolumeDiskStore'
import DataStorageStudyStatusStore from './dataStorage/config/dsStudyStatusStore'
import DataStorageStudyTagStore from './dataStorage/config/dsStudyTagStore'
import DataStorageSeriesTagStore from './dataStorage/config/dsSeriesTagStore'
import { StudyServiceStore, CustomColumnSchemaStore } from './dataStorage'

// Worklist Stores
import { PatientSearchStore } from './worklist/patientSearchStore'


//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface StoreProps {
  userAuth?: UserAuthStore
  user?: UserStore
  globalEvt?: EventStore
  group?: GroupStore
  dataStorage?: DataStorageStore
  node?: NodeStore
  registry?: RegistryStore
  dicomScp?: DicomScpStore
  hl7?: HL7Store
  wps?: WPSStore
  pacsqr?: PacsQRStore
  siteConfig?: SiteConfigStore
  systemConfig?: SystemConfigStore
  packageConfig?: PackageConfigStore
  dicompeers?: DicomPeerStore
  dsStudy?: DataStorageStudyStore
  dsSopInstance?: DataStorageSopInstanceStore
  dsSeries?: DataStorageSeriesStore
  dsPatient?: DataStoragePatientStore
  dsDataService?: DataStorageDataServiceStore
  dsConfigVolumeDisk?: DataStorageConfigVolumeDiskStore
  dsConfigIncomingVolumeDisk?: DataStorageConfigIncomingVolumeDiskStore
  license?: LicenseStore
  clApp?: CLAppStore
  apsRule?: APSRuleStore
  historyCtr?: HistoryStore
  apsBase?: APSBase
  dicomScpRulesetBase?: DicomScpRulesetBase
  dicomScpRuleset?: DicomScpRulesetStore
  product?: ProductStore
  dsConfigStudyStatus?: DataStorageStudyStatusStore
  dsStudyStatus?: DataStorageStudyStatusStore
  dsStudyTag?: DataStorageStudyTagStore
  userPref?: UserPrefStore
  dsSeriesTag?: DataStorageSeriesTagStore
  gateway?: GatewayStore
  connection?: ConnectionStore
  patientSearch?: PatientSearchStore
  studyService?: StudyServiceStore
  customColumnSchema?: CustomColumnSchemaStore
  notification?: NotificationStore
}

class Rootstore implements StoreProps {
  group?: GroupStore
  siteConfig?: SiteConfigStore
  systemConfig?: SystemConfigStore
  packageConfig?: PackageConfigStore
  userAuth?: UserAuthStore
  user?: UserStore
  globalEvt?: EventStore
  dataStorage?: DataStorageStore
  node?: NodeStore
  registry?: RegistryStore
  dicomScp?: DicomScpStore
  dicompeers?: DicomPeerStore
  hl7?: HL7Store
  wps?: WPSStore
  pacsqr?: PacsQRStore
  dsStudy?: DataStorageStudyStore
  dsSopInstance?: DataStorageSopInstanceStore
  dsSeries?: DataStorageSeriesStore
  dsPatient?: DataStoragePatientStore
  dsDataService?: DataStorageDataServiceStore
  dsConfigVolumeDisk?: DataStorageConfigVolumeDiskStore
  dsConfigIncomingVolumeDisk?: DataStorageConfigIncomingVolumeDiskStore
  license?: LicenseStore
  clApp?: CLAppStore
  apsRule?: APSRuleStore
  historyCtr?: HistoryStore
  apsBase?: APSBase
  dicomScpRulesetBase?: DicomScpRulesetBase
  dicomScpRuleset?: DicomScpRulesetStore
  product?: ProductStore
  dsConfigStudyStatus?: DataStorageStudyStatusStore
  dsStudyStatus?: DataStorageStudyStatusStore
  dsStudyTag?: DataStorageStudyTagStore
  userPref?: UserPrefStore
  dsSeriesTag?: DataStorageSeriesTagStore
  gateway?: GatewayStore
  connection?: ConnectionStore
  patientSearch?: PatientSearchStore
  studyService?: StudyServiceStore
  customColumnSchema?: CustomColumnSchemaStore
  notification?: NotificationStore

  constructor() {
    configure({
      enforceActions: 'never',
    })
    this.userAuth = new UserAuthStore(this)
    this.user = new UserStore(this)
    this.globalEvt = new EventStore(this)
    this.group = new GroupStore(this)
    this.dataStorage = new DataStorageStore(this)
    this.node = new NodeStore(this)
    this.registry = new RegistryStore(this)
    this.dicomScp = new DicomScpStore(this)
    this.siteConfig = new SiteConfigStore(this)
    this.systemConfig = new SystemConfigStore(this)
    this.packageConfig = new PackageConfigStore(this)
    this.dicompeers = new DicomPeerStore(this)
    this.hl7 = new HL7Store(this)
    this.wps = new WPSStore(this)
    this.pacsqr = new PacsQRStore(this)
    this.dsStudy = new DataStorageStudyStore(this)
    this.dsSopInstance = new DataStorageSopInstanceStore(this)
    this.dsSeries = new DataStorageSeriesStore(this)
    this.dsPatient = new DataStoragePatientStore(this)
    this.dsDataService = new DataStorageDataServiceStore(this)
    this.dsConfigVolumeDisk = new DataStorageConfigVolumeDiskStore(this)
    this.dsConfigIncomingVolumeDisk = new DataStorageConfigIncomingVolumeDiskStore(this)
    this.license = new LicenseStore(this)
    this.clApp = new CLAppStore(this)
    this.apsRule = new APSRuleStore(this)
    this.historyCtr = new HistoryStore(this)
    this.apsBase = new APSBase(this)
    this.dicomScpRulesetBase = new DicomScpRulesetBase(this)
    this.dicomScpRuleset = new DicomScpRulesetStore(this)
    this.product = new ProductStore(this)
    this.dsConfigStudyStatus = new DataStorageStudyStatusStore(this)
    this.dsStudyStatus = new DataStorageStudyStatusStore(this)
    this.dsStudyTag = new DataStorageStudyTagStore(this)
    this.userPref = new UserPrefStore(this)
    this.dsSeriesTag = new DataStorageSeriesTagStore(this)
    this.gateway = new GatewayStore(this)
    this.connection = new ConnectionStore(this)
    this.patientSearch = new PatientSearchStore(this)
    this.studyService = new StudyServiceStore(this)
    this.customColumnSchema = new CustomColumnSchemaStore(this)
    this.notification = new NotificationStore(this)
  }
}

export default Rootstore
export const rootStore = new Rootstore()
